import CloseIcon from '@mui/icons-material/Close'
import * as React from 'react'
import doordashLogo from '../../assets/doordash-logo.png'
import lyftLogo from '../../assets/lyft-logo.png'
import ubereatsLogo from '../../assets/ubereats-logo.png'
import { IconButton } from '../../shared-components/IconButton'
import { default as MuiIconButton } from '@mui/material/IconButton'
import { InputText } from '../../shared-components/InputText'
import { useScreenTracking } from '../../shared-hooks/useScreenTracking'
import { track } from '../../utils/analytics'
import { SignupCard } from './SignupCard'
import { REQUEST_OPTIONS, SignupStep } from './SignupFlow'
import { ErrorMessage } from '../../shared-components/ErrorMessage'

const WAITLIST_URL =
  'https://us-central1-infra-test-308817.cloudfunctions.net/waitlist'

const WAITLISTS = [
  { platform: 'Lyft', iconSrc: lyftLogo },
  { platform: 'UberEats', iconSrc: ubereatsLogo },
  { platform: 'Doordash', iconSrc: doordashLogo },
]

/**
 * Hook that makes it easier to update array compoenent state.
 */
const useArrayState = <T,>(
  initialState: T[]
): [T[], (i: number, value: T) => void] => {
  const [arrState, setArrState] = React.useState(initialState)

  const setArrValue = (i, value) => {
    setArrState((prevState) =>
      prevState.map((prevVal, prevValIndex) =>
        i === prevValIndex ? value : prevVal
      )
    )
  }
  return [arrState, setArrValue]
}

type WaitlistProps = {
  setSignupStep: (step: SignupStep) => void
}

export const Waitlist = ({ setSignupStep }: WaitlistProps): JSX.Element => {
  useScreenTracking('WaitlistView', 'View waitlist screen')

  const [phoneOrEmail, setPhoneOrEmail] = React.useState('')
  const [errorMessage, setErrorMessage] = React.useState('')
  const [hasJoinedWaitlistArray, setHasJoinedWaitlist] = useArrayState(
    WAITLISTS.map(() => false)
  )
  const [isSubmittingArray, setIsSubmitting] = useArrayState(
    WAITLISTS.map(() => false)
  )

  const joinWaitlist = async (i: number) => {
    setIsSubmitting(i, true)
    setErrorMessage('')

    track('JoinWaitlistAttempt', 'Join waitlist attempt')

    // Copied from stack overflow, but I trust.
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
    const phoneRegex = /^1?(\d{10})$/

    let isEmail: boolean
    if (emailRegex.test(phoneOrEmail)) {
      isEmail = true
    } else if (phoneRegex.test(phoneOrEmail.replace(/\D*/g, ''))) {
      isEmail = false
    } else {
      track('JoinWaitlistFailure', 'Join waitlist failure')
      setIsSubmitting(i, false)
      setErrorMessage('Please enter a valid phone number or email.')
      return
    }

    const { platform } = WAITLISTS[i]
    const bodyInfo = isEmail
      ? { email: phoneOrEmail }
      : { phone_number: phoneOrEmail.replace(/\D*/g, '') }
    const body = {
      ...bodyInfo,
      platforms: [platform],
      do_not_contact: false,
    }

    try {
      await fetch(WAITLIST_URL, {
        ...REQUEST_OPTIONS,
        body: JSON.stringify(body),
      })
      track('JoinWaitlistSuccess', 'Join waitlist success')
    } catch (error) {
      track('JoinWaitlistFailure', 'Join waitlist failure')
      // console.error(error)
      setIsSubmitting(i, false)
      return
    }

    setIsSubmitting(i, false)
    setHasJoinedWaitlist(i, true)
  }

  const isSubmitting = isSubmittingArray.some((isSubmitting) => isSubmitting)

  return (
    <SignupCard
      heading='Other Platforms'
      subheading='We currently only support UberX, and are working hard to integrate Eats into our app. You can still sign up but only times you drive with UberX enabled will be protected.'
      headerComponent={
        <div className='float-right relative -top-1.5 -right-4'>
          <MuiIconButton
            size='large'
            disabled={isSubmitting}
            onClick={() => setSignupStep(SignupStep.SSO)}
          >
            <CloseIcon fontSize='inherit' />
          </MuiIconButton>
        </div>
      }
    >
      <div className='-mt-2 gap-1 flex flex-col'>
        <InputText
          placeholder='Phone number or email'
          autoComplete='email'
          value={phoneOrEmail}
          onChange={(event) => setPhoneOrEmail(event.target.value)}
          disabled={isSubmitting}
          required
        />
        <ErrorMessage>{errorMessage}</ErrorMessage>
        {WAITLISTS.map(({ platform, iconSrc }, i) => {
          const iconButton = (
            <IconButton
              key={i}
              type='button'
              onClick={async () => joinWaitlist(i)}
              isSubmitting={isSubmittingArray[i]}
              disabled={hasJoinedWaitlistArray[i]}
              iconSrc={iconSrc}
            >
              {hasJoinedWaitlistArray[i]
                ? "You've joined the Waitlist!"
                : `Join ${platform} Waitlist`}
            </IconButton>
          )

          if (i === 0) {
            return (
              <div key={i} className='mt-2'>
                {iconButton}
              </div>
            )
          }
          return iconButton
        })}
      </div>
    </SignupCard>
  )
}
